/**
 * Phase 4.13 / 4.5.1 — ETA (estimated time of arrival) for an
 * in-flight batch.
 *
 * Pure-frontend computation derived from the items the API already
 * ships. Operator-facing surface lives on the BatchSummaryCard; this
 * module is just the math + formatting.
 *
 * Cost model (operator-provided rule of thumb):
 *   * one PPP task with one GNSS system → ~16 wall-clock minutes,
 *   * each additional system on the SAME task adds ~16 minutes,
 *   * PPP runs two tasks in parallel (the scheduler's submit
 *     concurrency cap).
 *
 * **Phase 4.5.1 corrected the formula.** The pre-4.5.1 version
 * was ``wall_clock = ⌈Σ(systems_per_task × 16) / 2⌉`` which
 * divided EACH task's duration by the parallelism — wrong, because
 * the parallelism only divides throughput across MULTIPLE tasks,
 * never the wall-clock for any single task. A 1-task × GPS batch
 * is 16 wall-clock minutes, not 8. The corrected formula:
 *
 *   wall_clock_minutes = ⌈N_tasks / parallelism⌉ × avg_task_duration
 *
 * Examples:
 *   * 1 task × GPS  → ⌈1/2⌉ × 16 = 16 min
 *   * 2 tasks × GPS → ⌈2/2⌉ × 16 = 16 min   (run in parallel)
 *   * 3 tasks × GPS → ⌈3/2⌉ × 16 = 32 min   (one slot waits a turn)
 *   * 1 task × all4 → ⌈1/2⌉ × 64 = 64 min
 *   * 4 tasks × all4 → ⌈4/2⌉ × 64 = 128 min
 *
 * The formula is still an UPPER BOUND — for a mixed-config batch
 * the "average task duration" assumption misses the case where
 * the longer task happens to be the bottleneck of its parallel
 * pair. Good enough for the operator's «примерно завершится через
 * X мин» surface; the alternative (per-task wall-clock simulation
 * with explicit slot allocation) would multiply the code for
 * marginal accuracy gain.
 *
 * Two helpers + the calculator:
 *   * ``calculateETA(items)`` — returns ``{ minutes, finishesAt }``
 *     or ``null`` when the batch has no remaining work.
 *   * ``etaFromSystemMinutes(systemMinutes, taskCount)`` — same
 *     formula but consumes the backend's pre-aggregated
 *     ``remaining_system_minutes`` + the count of remaining tasks
 *     (``counts.pending + counts.submitted`` on a ``BatchSummary``).
 *   * ``formatRelativeTime(minutes)`` → «через 32 мин» / «через 2ч 5мин».
 *   * ``formatTime(date)`` → «16:48» (HH:MM, browser's locale time).
 */

/** Cost rule of thumb: one GNSS system contributes 16 wall-clock
 *  minutes on PPP. Tuned by the operator; lives here so a future
 *  tweak is a single-line change. */
export const TIME_PER_SYSTEM_MIN = 16;

/** PPP-side parallelism — two tasks at a time. Matches the
 *  scheduler's submit-concurrency limit. */
export const PPP_PARALLELISM = 2;

/** A minimal JobSummary projection that ``calculateETA`` needs. The
 *  real type from ``api/types.ts`` has more fields; this interface
 *  keeps the calculator decoupled and trivially testable. */
export interface JobLike {
  state: string;
  gnss_systems?: readonly string[] | null;
}

export interface EstimatedTime {
  /** Remaining wall-clock minutes (upper-bound estimate). Always
   *  ≥ 1 when non-null — sub-minute residuals round up to keep the
   *  «через 0 мин» edge case off the UI. */
  minutes: number;
  /** Absolute timestamp ``now + minutes`` (browser-local Date). The
   *  caller renders this via ``formatTime`` for the «• HH:MM» suffix. */
  finishesAt: Date;
}

/** States that still consume wall-clock time. Anything else
 *  (``done`` / ``failed`` / ``cancelled``) is terminal — those jobs
 *  contribute zero to the estimate. */
const REMAINING_STATES: ReadonlySet<string> = new Set([
  'pending',
  'submitted',
]);

/** Compute an ETA for the batch represented by ``items``.
 *
 *  Returns ``null`` when every job is in a terminal state — the
 *  caller hides the ETA row entirely in that case (the spec's
 *  «не показывать ETA когда серия уже завершена» branch).
 *
 *  An empty / undefined ``gnss_systems`` on a remaining job is
 *  treated as 1 system (defensive — keeps the estimate from
 *  collapsing to zero when the API omits the field for some
 *  unrecognised state).
 *
 *  Used directly when the caller has per-job data (a future
 *  per-batch detail screen, the BatchModal preview). The
 *  BatchSummaryCard path is via ``etaFromSystemMinutes`` —
 *  it consumes the backend's pre-aggregated
 *  ``remaining_system_minutes`` field directly. */
export function calculateETA(
  items: readonly JobLike[],
  now: Date = new Date(),
): EstimatedTime | null {
  const remaining = items.filter((j) => REMAINING_STATES.has(j.state));
  if (remaining.length === 0) return null;

  const totalSystemMinutes = remaining.reduce((sum, job) => {
    const count = job.gnss_systems?.length || 1;
    return sum + count * TIME_PER_SYSTEM_MIN;
  }, 0);
  return etaFromSystemMinutes(totalSystemMinutes, remaining.length, now);
}

/** Same formula as ``calculateETA`` but consumes the pre-aggregated
 *  ``remaining_system_minutes`` value the backend ships on every
 *  ``BatchSummary``. Lets BatchSummaryCard render an ETA without
 *  fetching the per-job ``gnss_systems`` array N times.
 *
 *  Phase 4.5.1 — added ``taskCount`` (the remaining task count,
 *  ``counts.pending + counts.submitted``). The pre-4.5.1 signature
 *  took only ``systemMinutes``; the bug was that the formula tried
 *  to derive wall-clock from system-minutes alone, which collapses
 *  a 1-task × GPS batch to 8 min (correct: 16). The corrected
 *  formula is ``⌈N/parallelism⌉ × avgTaskDuration``, which needs
 *  both inputs.
 *
 *  Returns ``null`` when ``systemMinutes <= 0`` OR ``taskCount <= 0``
 *  — both happen when the backend's batch is fully terminal, which
 *  the Mini App reads as «hide the ETA row». */
export function etaFromSystemMinutes(
  systemMinutes: number,
  taskCount: number,
  now: Date = new Date(),
): EstimatedTime | null {
  if (!Number.isFinite(systemMinutes) || systemMinutes <= 0) return null;
  if (!Number.isFinite(taskCount) || taskCount <= 0) return null;
  // Average task duration (in minutes) is total-system-minutes
  // divided by task count. For a uniform-config batch this equals
  // ``len(gnss_systems) × TIME_PER_SYSTEM_MIN`` directly; for a
  // mixed-config batch it's a flat average — acceptable
  // approximation per the operator's «примерно» bar.
  const avgTaskDuration = systemMinutes / taskCount;
  // Wall-clock = how many parallel-slot "rounds" we need (ceiling
  // because the last round may be half-full) × the duration of one
  // task. Capped at 1 min minimum so a fractional-minute residual
  // doesn't render «через 0 мин».
  const wallClockMinutes = Math.max(
    1,
    Math.ceil(Math.ceil(taskCount / PPP_PARALLELISM) * avgTaskDuration),
  );
  return {
    minutes: wallClockMinutes,
    finishesAt: new Date(now.getTime() + wallClockMinutes * 60_000),
  };
}

/** «через 32 мин» / «через 2ч 5мин» / «через 1ч». Hours and minutes
 *  show only when non-zero so a flat 60-minute estimate renders as
 *  «через 1ч», not «через 1ч 0мин». */
export function formatRelativeTime(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `через ${m} мин`;
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  if (mins === 0) return `через ${hours}ч`;
  return `через ${hours}ч ${mins}мин`;
}

/** «16:48» — HH:MM in the browser's local time. Uses 2-digit zero-
 *  padding via ``toLocaleTimeString`` to keep the column-aligned
 *  look in the BatchSummaryCard footer. */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
